import { Card, Typography, Box, Tooltip } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useTheme } from "@mui/material/styles";
import { useAppContext } from "../../../2_ksbHub/context/appContext";
import { useSondertagContext } from "../context/sondertagContext";

export default function KlassenTile({
  klasseId,
  angebot,
  halbtag, // ✅ NEW
  onTileClick, // ✅ NEW (important)
  viewMode,
}) {
  const theme = useTheme();
  const { currentUser, isMobile, isTeacherView } = useAppContext();
  const { isASondertageEventAdmin } = useSondertagContext();

  /**
   * =========================================================
   * ✅ STATE LOGIC (NEW MODEL)
   * =========================================================
   */

  const isFree =
    !angebot ||
    !angebot.owners?.some((o) => o.abbreviation?.toLowerCase() !== "leer");

  const hasNoOwner = !angebot || !angebot.owners?.length;

  const isLeer =
    angebot?.owners?.length === 1 &&
    angebot.owners[0]?.abbreviation?.toLowerCase() === "leer";

  const isMine = angebot?.owners?.some((o) => o.id === currentUser?.id);

  const isLocked = !!angebot?.lockedBy;
  const isLockedByOther =
    angebot?.lockedBy && angebot.lockedBy !== currentUser?.id;

  const shouldHide =
    (viewMode === "mine" && !isMine) ||
    (viewMode === "leer" && !isLeer) ||
    (viewMode === "leitung" && !isFree);
  if (shouldHide) return null;

  /**
   * =========================================================
   * ✅ COLORS
   * =========================================================
   */
  let bgColor;
  let textColor = theme.palette.text.primary;

  if (isLocked) {
    bgColor = theme.palette.grey[300];
    textColor = theme.palette.text.secondary;
  } else if (isLeer) {
    bgColor = theme.palette.levels.orange;
  } else if (isFree) {
    bgColor = theme.palette.levels.lightGreen;
  } else if (isMine) {
    bgColor = theme.palette.levels.green;
    textColor = theme.palette.text.white;
  } else {
    bgColor = theme.palette.levels.green;
    textColor = theme.palette.text.white;
  }

  /**
   * =========================================================
   * ✅ TOOLTIP
   * =========================================================
   */
  const tooltipContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        // maxWidth: 200,
      }}
    >
      {isLockedByOther && (
        <Typography
          variant="caption"
          backgroundColor={theme.palette.error.main}
          color={theme.palette.text.primary}
        >
          🔒{" "}
          {angebot.owners?.length > 0
            ? angebot.owners.map((o) => o.abbreviation).join(", ")
            : angebot.locked_byUserAbbreviation}{" "}
          bearbeitet gerade
        </Typography>
      )}

      {angebot?.title && (
        <Typography variant="body2" fontWeight={600}>
          {angebot.title}
        </Typography>
      )}

      {angebot?.faecher?.length > 0 && (
        <Typography variant="caption" color={theme.palette.text.primary}>
          {angebot.faecher.length === 1 ? "Fach: " : "Fächer: "}
          {angebot.faecher.join(", ")}
        </Typography>
      )}

      {hasNoOwner ? (
        <Typography variant="caption" color={theme.palette.text.primary}>
          Noch kein Angebot für Klasse
        </Typography>
      ) : isLeer ? (
        <Typography variant="caption" color={theme.palette.text.primary}>
          Gesetzt, aber noch ohne LP
        </Typography>
      ) : (
        <Typography variant="caption" color={theme.palette.text.primary}>
          Verantwortlich: {angebot.owners.map((o) => o.abbreviation).join(", ")}
        </Typography>
      )}
    </Box>
  );

  /**
   * =========================================================
   * ✅ CLICK HANDLER
   * =========================================================
   */

  const canInteract =
    !isLockedByOther &&
    (isFree || !!angebot || (isASondertageEventAdmin && !isTeacherView));

  const handleClick = () => {
    if (!canInteract) return;

    onTileClick?.(angebot, klasseId, halbtag);
  };

  /**
   * =========================================================
   * ✅ RENDER
   * =========================================================
   */
  return (
    <Tooltip title={tooltipContent} arrow>
      <Card
        onClick={handleClick}
        sx={{
          width: "100%",
          minHeight: 64,

          cursor: canInteract ? "pointer" : "not-allowed",
          opacity: canInteract ? 1 : 0.5,

          bgcolor: bgColor,
          color: textColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 1,
          border: isMine
            ? `2px solid ${theme.palette.primary.light}`
            : "2px solid transparent",
        }}
      >
        <Box>
          <Typography fontWeight="bold" fontSize="0.9rem">
            {angebot?.specialTitle ? angebot.specialTitle : klasseId}
          </Typography>

          {angebot?.title && (
            <Typography variant="caption" noWrap>
              {isMobile && angebot.title.length > 12
                ? angebot.title.slice(0, 12) + "…"
                : angebot.title}
            </Typography>
          )}

          {isLocked && (
            <Box mt={0.5}>
              <LockIcon fontSize="small" />
            </Box>
          )}
        </Box>
      </Card>
    </Tooltip>
  );
}
